import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Home, Pencil, Layers, RefreshCw } from "lucide-react";
import { api, formatApiError } from "../../lib/api";
import useBulkSelect from "../../lib/useBulkSelect";
import BulkActionBar, { BulkCheckbox } from "../../components/BulkActionBar";
import BulkBookingModal from "../../components/BulkBookingModal";
import IcalFeedsModal from "../../components/IcalFeedsModal";
import PostalAddressFields, { addressFromApt, EMPTY_ADDRESS } from "../../components/PostalAddressFields";

const emptyForm = { name: "", apt_type: "T0", ...EMPTY_ADDRESS, notes: "" };

export default function AdminApartments() {
  const [items, setItems] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [bulkBookingApt, setBulkBookingApt] = useState(null); // apartmentId or null
  const [feedsApt, setFeedsApt] = useState(null); // {id, name} or null

  const load = () => api.get("/apartments").then(({ data }) => setItems(data.items));
  useEffect(() => { load(); }, []);

  function resetForm() {
    setForm(emptyForm);
    setEditingId(null);
    setShowForm(false);
  }

  function startCreate() {
    setForm(emptyForm);
    setEditingId(null);
    setShowForm(true);
  }

  function startEdit(apt) {
    setForm({
      name: apt.name || "",
      apt_type: apt.apt_type || "T0",
      ...addressFromApt(apt),
      notes: apt.notes || "",
    });
    setEditingId(apt.id);
    setShowForm(true);
  }

  async function submit(e) {
    e.preventDefault();
    try {
      if (editingId) {
        await api.put(`/apartments/${editingId}`, form);
        toast.success("Apartamento atualizado");
      } else {
        await api.post("/apartments", form);
        toast.success("Apartamento criado");
      }
      resetForm();
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  }

  async function remove(id) {
    if (!window.confirm("Eliminar este apartamento?")) return;
    try {
      await api.delete(`/apartments/${id}`);
      toast.success("Eliminado");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  }

  const ids = useMemo(() => items.map(a => a.id), [items]);
  const bulk = useBulkSelect(ids);
  async function bulkRun(selected, fn, { successMsg }) {
    const res = await Promise.allSettled(selected.map(fn));
    const ok = res.filter(r => r.status === "fulfilled").length;
    const ko = res.length - ok;
    if (ok > 0) toast.success(`${ok} ${successMsg}${ko > 0 ? ` · ${ko} falharam` : ""}`);
    else toast.error("Nenhuma operação concluída");
    load();
  }

  return (
    <div className="space-y-8">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="eyebrow mb-3">Imóveis</div>
          <h1 className="font-display text-5xl">Apartamentos</h1>
        </div>
        <button onClick={startCreate} className="btn-gold px-5 py-3 text-sm inline-flex items-center gap-2" data-testid="apt-new">
          <Plus size={16} /> Novo apartamento
        </button>
      </div>

      {showForm && (
        <form onSubmit={submit} className="lux-card p-6 grid md:grid-cols-2 gap-4" data-testid="apt-form">
          <div className="md:col-span-2 eyebrow">
            {editingId ? "Editar apartamento" : "Novo apartamento"}
          </div>
          <div className="space-y-2">
            <label className="text-xs uppercase tracking-widest text-[#a0b2af]">Nome / Designação</label>
            <input required value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                   className="w-full bg-[#062c27] border border-[#1a4640] rounded-sm px-4 py-3"
                   data-testid="apt-name" />
          </div>
          <div className="space-y-2">
            <label className="text-xs uppercase tracking-widest text-[#a0b2af]">Tipo</label>
            <select value={form.apt_type} onChange={e => setForm(f => ({ ...f, apt_type: e.target.value }))}
                    className="w-full bg-[#062c27] border border-[#1a4640] rounded-sm px-4 py-3"
                    data-testid="apt-type">
              {[
                ["T0", "T0 · Studio"],
                ["T1", "T1"],
                ["T2", "T2"],
                ["T3", "T3"],
                ["T4", "T4"],
                ["T5", "T5"],
                ["VS", "Villa pequena"],
                ["VM", "Villa média"],
                ["VL", "Villa grande / premium"],
                ["HOTEL", "Hotel / Alojamento múltiplo"],
              ].map(([t, label]) => <option key={t} value={t}>{label}</option>)}
            </select>
          </div>
          <PostalAddressFields value={form} onChange={(v) => setForm(f => ({ ...f, ...v }))} />
          <div className="md:col-span-2 space-y-2">
            <label className="text-xs uppercase tracking-widest text-[#a0b2af]">Notas</label>
            <textarea rows={2} value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
                      className="w-full bg-[#062c27] border border-[#1a4640] rounded-sm px-4 py-3"
                      data-testid="apt-notes" />
          </div>
          <div className="md:col-span-2 flex gap-3">
            <button type="submit" className="btn-gold px-6 py-3 text-sm" data-testid="apt-submit">
              {editingId ? "Guardar alterações" : "Criar"}
            </button>
            <button type="button" onClick={resetForm} className="px-6 py-3 text-sm text-[#a0b2af]">Cancelar</button>
          </div>
        </form>
      )}

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.length > 0 && (
          <label className="col-span-full inline-flex items-center gap-2 px-3 py-2 text-[10px] uppercase tracking-widest text-[#a0b2af] border border-[#1a4640] rounded-sm w-fit cursor-pointer hover:text-[#d4af37] hover:border-[#d4af37]/50">
            <BulkCheckbox
              checked={bulk.allSelected}
              indeterminate={bulk.someSelected}
              onChange={() => bulk.toggleAll(ids)}
              label="Selecionar todos"
            />
            Selecionar todos
          </label>
        )}
        {items.length === 0 && (
          <div className="lux-card p-12 text-center text-[#a0b2af] col-span-full">
            <Home className="mx-auto mb-4 text-[#d4af37]" size={28} />
            <div>Sem apartamentos registados. Crie o primeiro.</div>
          </div>
        )}
        {items.map(a => (
          <div key={a.id}
               className={`lux-card p-6 transition-colors ${bulk.isSelected(a.id) ? "border-[#d4af37]" : ""}`}
               data-testid={`apt-card-${a.id}`}>
            <div className="flex items-start justify-between gap-3 mb-3">
              <div className="flex items-start gap-3 min-w-0">
                <BulkCheckbox
                  id={a.id}
                  checked={bulk.isSelected(a.id)}
                  onChange={() => bulk.toggle(a.id)}
                  label="Selecionar apartamento"
                />
                <div className="min-w-0">
                  <div className="eyebrow">{a.apt_type}</div>
                  <div className="font-display text-2xl mt-1 break-words" style={{ overflowWrap: "anywhere" }}>{a.name}</div>
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <button onClick={() => setBulkBookingApt(a.id)}
                        className="text-[#d4af37] hover:text-white inline-flex items-center gap-1 text-xs px-2 py-1 border border-[#1a4640] rounded-sm"
                        data-testid={`apt-bulk-bookings-${a.id}`}
                        title="Criar múltiplos agendamentos para este alojamento">
                  <Layers size={14} /> Agenda
                </button>
                <button onClick={() => setFeedsApt({ id: a.id, name: a.name })}
                        className="text-[#10b981] hover:text-white inline-flex items-center gap-1 text-xs px-2 py-1 border border-[#1a4640] rounded-sm"
                        data-testid={`apt-ical-feeds-${a.id}`}
                        title="Sincronizar automaticamente com Booking.com / Airbnb / Vrbo">
                  <RefreshCw size={14} /> OTA
                </button>
                <button onClick={() => startEdit(a)} className="text-[#d4af37] hover:text-white"
                        data-testid={`apt-edit-${a.id}`} title="Editar">
                  <Pencil size={16} />
                </button>
                <button onClick={() => remove(a.id)} className="text-[#fca5a5] hover:text-[#ef4444]"
                        data-testid={`apt-delete-${a.id}`} title="Eliminar">
                  <Trash2 size={16} />
                </button>
              </div>
            </div>
            {a.address && <div className="text-sm text-[#a0b2af]">{a.address}</div>}
            {a.notes && <div className="text-xs text-[#a0b2af] mt-2 italic">{a.notes}</div>}
          </div>
        ))}
      </div>

      <BulkActionBar
        count={bulk.count}
        ids={Array.from(bulk.selected)}
        onClear={bulk.clear}
        actions={[
          {
            key: "delete", label: "Eliminar", icon: Trash2, variant: "danger",
            confirm: "Eliminar {count} apartamento(s)? Ação irreversível.",
            onClick: (sel) => bulkRun(sel, (id) => api.delete(`/apartments/${id}`), { successMsg: "eliminado(s)" }),
          },
        ]}
      />

      <BulkBookingModal open={!!bulkBookingApt}
                        apartmentId={bulkBookingApt}
                        onClose={() => setBulkBookingApt(null)}
                        onSuccess={() => setBulkBookingApt(null)} />

      <IcalFeedsModal open={!!feedsApt}
                      apartmentId={feedsApt?.id}
                      apartmentName={feedsApt?.name}
                      onClose={() => setFeedsApt(null)} />
    </div>
  );
}
